import React from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { HelpCircle } from "lucide-react";

const FAQSection = () => {
  const faqs = [
    {
      question: "Qu'est-ce qu'un courtier en énergie ?",
      answer: "Un courtier en énergie est un **intermédiaire indépendant** qui analyse votre consommation, compare les offres du marché et négocie **les meilleurs contrats d'électricité et de gaz** pour vous. Son objectif est de **réduire vos coûts énergétiques** en trouvant la solution la plus adaptée, sans engagement auprès d'un fournisseur spécifique."
    },
    {
      question: "Pourquoi choisir EASY POWER plutôt qu'un autre courtier ?",
      answer: "Nous sommes **100% indépendants** des fournisseurs, ce qui nous permet de vous proposer **les offres réellement avantageuses**. En plus de comparer et négocier vos contrats, nous optimisons votre **puissance souscrite** et vous aidons à **bénéficier des CEE disponibles**. Notre expertise de 8 ans et nos 2500+ clients satisfaits témoignent de notre efficacité."
    },
    {
      question: "Quelles économies puis-je espérer ?",
      answer: "Les économies varient selon votre profil de consommation. Nos clients constatent en moyenne **15% à 35% de réduction** sur leur facture énergétique, parfois plus avec une optimisation complète des puissances et des heures tarifaires. Les secteurs industriels et les copropriétés obtiennent généralement les meilleures économies."
    },
    {
      question: "Combien coûtent vos services ?",
      answer: "Notre modèle est **simple et transparent** :\n✅ L'analyse et le conseil sont gratuits\n✅ Nous sommes rémunérés par les fournisseurs lors de la signature\n✅ Aucun frais caché, aucun engagement\n✅ Vous ne payez que si vous réalisez des économies"
    },
    {
      question: "Comment commencer ?",
      answer: "Contactez-nous via notre formulaire ou par téléphone au 04 65 84 83 05. Nos experts analyseront votre situation et vous proposeront **une étude gratuite et sans engagement** pour réduire vos coûts énergétiques. L'audit prend généralement 48h et vous recevez un rapport détaillé avec nos recommandations."
    },
    {
      question: "Qu'est-ce que les Certificats d'Économies d'Énergie (CEE) ?",
      answer: "Les CEE sont des aides publiques qui financent vos travaux d'efficacité énergétique. En tant que courtier agréé, nous vous aidons à identifier et obtenir ces **financements gratuits** qui peuvent couvrir jusqu'à 50% de vos investissements énergétiques (isolation, éclairage LED, systèmes de chauffage, etc.)."
    },
    {
      question: "Travaillez-vous avec tous les fournisseurs d'énergie ?",
      answer: "Oui, nous travaillons avec **tous les principaux fournisseurs** du marché français : EDF, Engie, TotalEnergies, Eni, Vattenfall, Iberdrola, et bien d'autres. Cette indépendance totale nous permet de négocier les **meilleures conditions** pour chaque client selon son profil spécifique."
    },
    {
      question: "Combien de temps prend l'optimisation de mes contrats ?",
      answer: "Le processus complet prend généralement **2 à 4 semaines** :\n• Audit initial : 48h\n• Négociation avec les fournisseurs : 1-2 semaines\n• Finalisation et signature : 1 semaine\n• Mise en service : automatique, sans coupure\nVous commencez à économiser dès le premier mois !"
    }
  ];

  const formatAnswer = (answer) => {
    return answer.split('\n').map((line, lineIndex) => {
      const parts = line.split('**');
      return (
        <div key={lineIndex} className={lineIndex > 0 ? "mt-2" : ""}>
          {parts.map((part, partIndex) => {
            if (partIndex % 2 === 1) {
              return <strong key={partIndex} className="text-blue-600">{part}</strong>;
            }
            return part;
          })}
        </div>
      );
    });
  };

  return (
    <section id="faq" className="py-20 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center p-3 bg-blue-100 rounded-full mb-6">
            <HelpCircle className="h-8 w-8 text-blue-600" />
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Questions Fréquentes
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Tout ce que vous devez savoir sur le courtage énergétique et nos services
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="bg-white rounded-2xl shadow-lg border-0 overflow-hidden">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`} className="border-b border-gray-100 last:border-b-0">
                <AccordionTrigger className="px-8 py-6 text-left hover:bg-gray-50 transition-colors duration-200">
                  <span className="text-lg font-semibold text-gray-900 leading-relaxed">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-8 pb-6">
                  <div className="text-gray-700 leading-relaxed text-base">
                    {formatAnswer(faq.answer)}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Additional Help Section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-blue-600 to-green-600 text-white py-8 px-8 rounded-2xl shadow-xl">
            <h3 className="text-2xl font-bold mb-4">
              Vous avez d'autres questions ?
            </h3>
            <p className="text-xl text-blue-100 mb-6">
              Nos experts sont là pour vous répondre et vous accompagner
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => document.querySelector("#contact").scrollIntoView({ behavior: "smooth" })}
                className="bg-white text-blue-600 hover:bg-gray-100 px-6 py-3 rounded-lg font-semibold transition-colors duration-200 shadow-lg"
              >
                Nous contacter
              </button>
              <a
                href="tel:0465848305"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-blue-600 px-6 py-3 rounded-lg font-semibold transition-all duration-200"
              >
                04 65 84 83 05
              </a>
            </div>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-12 grid md:grid-cols-3 gap-8 text-center">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-green-600 mb-2">100%</div>
            <div className="text-gray-600">Gratuit et sans engagement</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-blue-600 mb-2">48h</div>
            <div className="text-gray-600">Délai de réponse maximum</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <div className="text-3xl font-bold text-purple-600 mb-2">32 ans</div>
            <div className="text-gray-600">D'expertise reconnue</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;